import { useCallback, useEffect, useRef, useState } from 'react';
import { StockCategoryFilter } from '../types/stockCategories';
import { TimerResult, TimerState } from '../types/game';
import { localStorageUtil } from '../utils/localStorage';

const TIMER_DELAY_MS = 5_000;
const MAX_TIME_MS = 1_800_000;

interface UseCategoryTimerOptions {
  category: StockCategoryFilter;
  gameDate: string;
  enabled?: boolean;
}

export const useCategoryTimer = ({
  category,
  gameDate,
  enabled = true
}: UseCategoryTimerOptions) => {
  const [timerState, setTimerState] = useState<TimerState | null>(null);
  const [liveElapsedMs, setLiveElapsedMs] = useState<number>(0);
  const [countdownSeconds, setCountdownSeconds] = useState<number>(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const storageKey = `ws:timer:${category}:${gameDate}`;

  const hydrateState = useCallback(() => {
    if (!enabled) {
      setTimerState(null);
      return;
    }

    const savedState = localStorageUtil.getItem<TimerState>(storageKey);

    if (savedState) {
      setTimerState(savedState);
      return;
    }

    const now = Date.now();
    const newState: TimerState = {
      status: 'armed',
      enteredAt: now,
      startAt: now + TIMER_DELAY_MS,
      stoppedAt: null
    };

    setTimerState(newState);
    localStorageUtil.setItem(storageKey, newState);
  }, [enabled, storageKey]);

  useEffect(() => {
    hydrateState();
  }, [hydrateState]);

  useEffect(() => {
    if (!timerState || timerState.status === 'stopped') {
      return;
    }

    const tick = () => {
      const now = Date.now();

      if (timerState.status === 'armed' && now >= timerState.startAt) {
        const updatedState: TimerState = { ...timerState, status: 'running' };
        setTimerState(updatedState);
        localStorageUtil.setItem(storageKey, updatedState);
      }
    };

    intervalRef.current = setInterval(tick, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [timerState, storageKey]); // Keep timerState dependency for correctness

  // Track countdown phase (5, 4, 3, 2, 1, GO!)
  useEffect(() => {
    if (timerState?.status !== 'armed') {
      setCountdownSeconds(0);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, timerState.startAt - Date.now());
      setCountdownSeconds(Math.ceil(remaining / 1000));
    };

    updateCountdown(); // Initial update
    const countdownInterval = setInterval(updateCountdown, 100); // Smooth 100ms updates

    return () => clearInterval(countdownInterval);
  }, [timerState?.status, timerState?.startAt]);

  // Track live elapsed time during gameplay
  useEffect(() => {
    if (timerState?.status !== 'running') {
      setLiveElapsedMs(0);
      return;
    }

    const updateElapsed = () => {
      const elapsed = Date.now() - timerState.startAt;
      setLiveElapsedMs(Math.min(elapsed, MAX_TIME_MS));
    };

    updateElapsed(); // Initial update
    const elapsedInterval = setInterval(updateElapsed, 1000); // Update every second

    return () => clearInterval(elapsedInterval);
  }, [timerState?.status, timerState?.startAt]);

  const stopTimer = useCallback((): TimerResult => {
    if (!timerState) {
      return { elapsedMs: 0, wasStarted: false };
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    const now = Date.now();
    const stoppedState: TimerState = {
      ...timerState,
      status: 'stopped',
      stoppedAt: now
    };

    const wasStarted = now >= timerState.startAt;
    const elapsedMs = wasStarted ? Math.min(now - timerState.startAt, MAX_TIME_MS) : 0;

    setTimerState(stoppedState);
    localStorageUtil.setItem(storageKey, stoppedState);

    return { elapsedMs, wasStarted };
  }, [timerState, storageKey]);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    localStorageUtil.removeItem(storageKey);

    if (!enabled) {
      setTimerState(null);
      return;
    }

    const now = Date.now();
    const newState: TimerState = {
      status: 'armed',
      enteredAt: now,
      startAt: now + TIMER_DELAY_MS,
      stoppedAt: null
    };

    setTimerState(newState);
    localStorageUtil.setItem(storageKey, newState);
  }, [enabled, storageKey]);

  return {
    timerState,
    stopTimer,
    resetTimer,
    // New fields for UI display
    isCountingDown: timerState?.status === 'armed' && countdownSeconds > 0,
    countdownSeconds,
    isRunning: timerState?.status === 'running',
    liveElapsedMs
  };
};
